// src/services/approvalHold.ts
// ----------------------------
// "Monitored shelter" approval hold. When a shelter is flagged
// shelter_notes.no_approvals ("No requests for this shelter to be approved —
// refer to management"), a user WITHOUT the `monitored_approval` capability may not
// set an item to Approved or Declined — only Needs Info. A capability holder (and
// every SuperUser) is exempt (the escalation target). Callers compute that boolean
// (`isSuperUser(user) || canAccess(user, "monitored_approval")`) and pass it in, so
// this stays pure. Was "any admin exempt" until 6 Sep 2026 — see
// docs/permissions-and-approval-hold-plan.md §Part 1.
// The message shown is admin-editable in System Content → Monitoring
// (portal_config.monitored_shelter_alert). UI wiring lives in ResponderChildView
// (per-selection) + ApprovalDecisions (Confirm & Send backstop).
// Design: docs/monitored-shelter-approval-hold-plan.md.

import { APPROVAL_STATUSES } from "../utils/enums";

export const MONITORED_SHELTER_ALERT_KEY = "monitored_shelter_alert";

export const DEFAULT_MONITORED_SHELTER_ALERT =
  'This DFV Service is being monitored by Admin. You can ONLY select "Needs Info". ' +
  "You should then Save & Send to DFV Service. Once this is done you should assign this " +
  "Request to an Administrator on the Task List Screen.";

function isDecided(status: unknown): boolean {
  return status === APPROVAL_STATUSES.APPROVED || status === APPROVAL_STATUSES.DECLINED;
}

// True when this attempt to set `status` must be blocked: the shelter is on hold, the
// user MAY NOT approve monitored services, and the target status is Approved or
// Declined. `canApproveMonitored` is computed by the caller (SuperUser OR the
// monitored_approval capability).
export function isApprovalActionBlocked(
  canApproveMonitored: boolean,
  noApprovals: boolean,
  status: unknown
): boolean {
  if (!noApprovals) return false;
  if (canApproveMonitored) return false;
  return isDecided(status);
}

// A child's four section slots, as far as the scan cares.
interface HoldChild {
  fees?: { approvalStatus?: string }[] | null;
  uniforms?: { approvalStatus?: string } | null;
  shoes?: { approvalStatus?: string } | null;
  laptop?: { approvalStatus?: string } | null;
}

// True if ANY item in the children tree is Approved or Declined (the Confirm &
// Send backstop — catches items decided before the flag went on, or by an admin).
export function hasApprovedOrDeclinedItems(children: HoldChild[] | null | undefined): boolean {
  if (!Array.isArray(children)) return false;
  return children.some((c) => {
    if ((c.fees || []).some((f) => isDecided(f?.approvalStatus))) return true;
    if (c.uniforms && isDecided(c.uniforms.approvalStatus)) return true;
    if (c.shoes && isDecided(c.shoes.approvalStatus)) return true;
    if (c.laptop && isDecided(c.laptop.approvalStatus)) return true;
    return false;
  });
}
